import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Play, User, Trophy, LogIn, UserPlus, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: ${({ theme }) => theme.colors.surface};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  z-index: 100;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 767px) {
    padding: 0 8px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;

  ${({ theme }) => theme.breakpoints.mobile} {
    height: 32px;
  }
`;

const LogoText = styled.h1`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;

  @media (max-width: 767px) {
    font-size: 1.1rem;
  }
`;

const LogoAccent = styled.span`
  color: ${({ theme }) => theme.colors.accent};
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    display: none;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 767px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.surface};
    border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing.md};
    gap: ${({ theme }) => theme.spacing.sm};
    box-shadow: ${({ theme }) => theme.shadows.lg};
    z-index: 99;
  }
`;

const MobileNavLink = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: 600;
  width: 100%;
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const NavLink = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 70px;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 767px) {
    min-width: 50px;
    padding: 6px 2px;
    font-size: 0.65rem;
  }
`;

const NavIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 767px) {
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const NavLabel = styled.span`
  @media (max-width: 767px) {
    display: none;
  }
`;

// Add padding to page content to account for fixed nav
export const PageContent = styled.div`
  padding-top: 80px;
  min-height: 100vh;
`;

interface NavigationProps {
  hideOnRoutes?: string[];
}

const Navigation: React.FC<NavigationProps> = ({ hideOnRoutes = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hide navigation on specific routes
  if (hideOnRoutes.some(route => location.pathname.startsWith(route))) {
    return null;
  }

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogoClick = () => {
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <NavContainer>
        <Logo onClick={handleLogoClick}>
          <LogoText>
            Bible <LogoAccent>Duel</LogoAccent>
          </LogoText>
        </Logo>

        <HamburgerButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X /> : <Menu />}
        </HamburgerButton>

        <NavLinks>
        {currentUser ? (
          <>
            <NavLink onClick={() => navigate('/')} $active={isActive('/')}>
              <NavIcon>
                <Home />
              </NavIcon>
              <NavLabel>Home</NavLabel>
            </NavLink>

            <NavLink onClick={() => navigate('/modes')} $active={isActive('/modes') || isActive('/game')}>
              <NavIcon>
                <Play />
              </NavIcon>
              <NavLabel>Play</NavLabel>
            </NavLink>

            <NavLink onClick={() => navigate('/profile')} $active={isActive('/profile')}>
              <NavIcon>
                <User />
              </NavIcon>
              <NavLabel>Profile</NavLabel>
            </NavLink>

            <NavLink onClick={() => navigate('/leaderboard')} $active={isActive('/leaderboard')}>
              <NavIcon>
                <Trophy />
              </NavIcon>
              <NavLabel>Ranks</NavLabel>
            </NavLink>
          </>
        ) : (
          <>
            <NavLink onClick={() => navigate('/')} $active={isActive('/')}>
              <NavIcon>
                <Home />
              </NavIcon>
              <NavLabel>Home</NavLabel>
            </NavLink>

            <NavLink onClick={() => navigate('/login')} $active={isActive('/login')}>
              <NavIcon>
                <LogIn />
              </NavIcon>
              <NavLabel>Login</NavLabel>
            </NavLink>

            <NavLink onClick={() => navigate('/register')} $active={isActive('/register')}>
              <NavIcon>
                <UserPlus />
              </NavIcon>
              <NavLabel>Sign Up</NavLabel>
            </NavLink>
          </>
        )}
      </NavLinks>
    </NavContainer>

    <MobileMenu $isOpen={mobileMenuOpen}>
      {currentUser ? (
        <>
          <MobileNavLink onClick={() => handleNavigate('/')} $active={isActive('/')}>
            <Home />
            <span>Home</span>
          </MobileNavLink>

          <MobileNavLink onClick={() => handleNavigate('/modes')} $active={isActive('/modes') || isActive('/game')}>
            <Play />
            <span>Play</span>
          </MobileNavLink>

          <MobileNavLink onClick={() => handleNavigate('/profile')} $active={isActive('/profile')}>
            <User />
            <span>Profile</span>
          </MobileNavLink>

          <MobileNavLink onClick={() => handleNavigate('/leaderboard')} $active={isActive('/leaderboard')}>
            <Trophy />
            <span>Ranks</span>
          </MobileNavLink>
        </>
      ) : (
        <>
          <MobileNavLink onClick={() => handleNavigate('/')} $active={isActive('/')}>
            <Home />
            <span>Home</span>
          </MobileNavLink>

          <MobileNavLink onClick={() => handleNavigate('/login')} $active={isActive('/login')}>
            <LogIn />
            <span>Login</span>
          </MobileNavLink>

          <MobileNavLink onClick={() => handleNavigate('/register')} $active={isActive('/register')}>
            <UserPlus />
            <span>Sign Up</span>
          </MobileNavLink>
        </>
      )}
    </MobileMenu>
  </>
  );
};

export default Navigation;
